import { Button, message, Popover, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ReactComponent as RightArrow } from '../../../Assets/CommonTenant/rightCircle-arrow.svg';
import { formatToINR, formatToINRForStocks } from '../../../Utils/utils';
import { getMaskedAmount } from '../common/MaskedAmount';
import {
  getChartColors,
  getTenantName,
  hasStocksAnalytics,
} from '../utils/configUtils';
import { getStocksTotalValue } from '../utils/calculationUtils';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Sheet } from 'react-modal-sheet';
import { useNavigate } from 'react-router-dom';
import { AppContext, RefreshStatus } from '../../../App';
import LoadingGIF from '../../../Assets/loading.gif';
import { ReactComponent as FilterSorting } from '../../../Assets/slider.svg';
import {
  refreshApi,
  revokeConsentAPI,
} from '../../../Services/PFMClient/DashboardService';
import { handleImageError, getStockLogoSrc } from '../utils/imageUtils';
import { FlowType } from '../../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../../Utils/eventUtils';
import { ROUTES } from '../../../Utils/routes';
import HeaderSubHeader from '../Header/HeaderSubHeader';
import AssetTopSection from '../common/AssetTopSection';
import NavigationHeader from '../Header/NavigationHeader';
import HoldingsScreen from './HoldingsScreen';
import styles from './index.module.css';
import SheetLinkedAccountItem from '../common/SheetLinkedAccountItem';

interface Props {
  onHoldingClick?: (holding: any) => void;
}

const StocksNewFlow: React.FC<Props> = ({ onHoldingClick }) => {
  const navigate = useNavigate();
  const [selectedHolding, setSelectedHolding] = useState<any>(null);
  const {
    stocksData,
    data,
    setStocksData,
    startAdditionalAccountJourney,
    statusData,
    setStatusPolling,
    setIsAccountDetailsFetched,
    setAnalyticsStatusStocks,
    setAnalyticsStatus,
    dataRefreshStatus,
    isAmountVisible,
    setBackButtonHandler,
    setCurrentRoute,
    isAssetDashboardRendered,
    forceRender,
  }: any = useContext(AppContext);
  const accountMap = new Map(
    statusData?.current?.accountDetails?.map((account: any) => [
      account.lastRawDataId,
      account.fipId,
    ]) || []
  );

  const modifiedStocksData = stocksData?.map((stock: any) => ({
    ...stock,
    fipId: accountMap.get(stock.dataRequestDetailsId) ?? null,
  }));

  const marketCapKeyToDisplay: Record<string, string> = {
    SMALL_CAP: 'Small Cap',
    MID_CAP: 'Mid Cap',
    LARGE_CAP: 'Large Cap',
  };

  const showAnalytics = hasStocksAnalytics();

  const equitiesETFData = data?.current?.EQUITIES_ETF;

  const holdingsData = useMemo(() => {
    const stockAnalysis =
      equitiesETFData?.data?.equityAnalysis?.stockWiseAnalysis;

    if (showAnalytics && !stockAnalysis) return null;

    if (showAnalytics && stockAnalysis) {
      const holdings = stockAnalysis.map((stock: any) => ({
        companyName: stock.companyName,
        bsePrices: String(stock.currentStockPrice || 0),
        units: stock.units || 0,
        marketCap: stock.marketCapitalizationType,
        percentageChange: stock.absolutePercentageReturn || 0,
        logo: stock.logo,
        sector: stock.sector,
        date: stock.date || new Date().toISOString(),
        lastWeekPL: stock.lastWeekPL || 0,
        oneWeekPercentage: stock.oneWeekPercentage || null,
        peRatio: stock.peRatio || null,
        sectorPeRatio: stock.trailing12MonthsPERatio || null,
        oneMonthPercentage: stock.oneMonthPercentage || null,
        threeMonthPercentage: stock.threeMonthPercentage || null,
        oneYearPercentage: stock.oneYearPercentage || null,
        prosConsDTOList: stock.prosConsDTOList || [],
      }));

      const overallAnalysis = equitiesETFData?.data?.combinedOverallAnalysis;
      const mcapAnalysis =
        overallAnalysis?.overallEquityOnlyMarketCapBasedAnalysis;
      const marketCap: Record<string, string> = {};
      if (mcapAnalysis) {
        if (mcapAnalysis.LARGE_CAP)
          marketCap.LARGE_CAP = `${Math.round(mcapAnalysis.LARGE_CAP.percentageContribution)}%`;
        if (mcapAnalysis.MID_CAP)
          marketCap.MID_CAP = `${Math.round(mcapAnalysis.MID_CAP.percentageContribution)}%`;
        if (mcapAnalysis.SMALL_CAP)
          marketCap.SMALL_CAP = `${Math.round(mcapAnalysis.SMALL_CAP.percentageContribution)}%`;
      }
      return { holdings, marketCap };
    }

    // Raw data mode (no analytics) — build holdings from stocksData
    const map = new Map<string, any>();
    modifiedStocksData?.forEach((stock: any) => {
      const type = stock.rawData?.type?.toLowerCase() || 'equities';
      const list = stock.rawData?.Summary?.Investment?.Holdings?.Holding || [];
      list.forEach((h: any) => {
        const key = `${h.isin}_${type}`;
        if (map.has(key)) return;
        const ltp =
          type === 'etf'
            ? parseFloat(h.nav || '0')
            : parseFloat(h.lastTradedPrice || '0');
        const units = parseFloat(h.units || '0');
        map.set(key, {
          companyName: h.issuerName ?? h.isinDescription ?? '',
          bsePrices: String(ltp),
          units,
          logo: null,
          isin: h.isin,
          date: new Date().toISOString(),
          type,
          brokerName: stock.rawData.brokerName ?? stock.rawData.fipName,
        });
      });
    });

    const holdings = Array.from(map.values()).sort(
      (a, b) =>
        parseFloat(b.bsePrices) * b.units - parseFloat(a.bsePrices) * a.units
    );

    return { holdings, marketCap: {} };
  }, [equitiesETFData, modifiedStocksData, showAnalytics]);

  const refreshData = () => {
    if (
      statusData.current.accountDetails.filter((accountDetail: any) => {
        return accountDetail.status === 'REVOKE_INITIATED';
      }).length !== 0
    ) {
      message.info(
        <Typography.Text style={{ fontSize: 10, fontWeight: 600 }}>
          Consent revocation in progress. Please try again in some time.
        </Typography.Text>
      );
    } else {
      pushEventToParent({
        eventCode: EVENTCODES.USER_INITIATED_REFRESH_BUTTON_CLICKED,
      });
      refreshApi()
        .then(() => {
          setAnalyticsStatus('PENDING');
          setIsAccountDetailsFetched(false);
          setStatusPolling((prev: any) => prev + 1);
          pushEventToParent({
            eventCode: EVENTCODES.REFRESH_PROCESSING,
          });
        })
        .catch(() => {
          pushEventToParent({
            eventCode: EVENTCODES.REFRESH_FAILED,
          });
          message.error('Something went wrong. Try again after sometime');
        });
    }
  };

  const refreshTime = useMemo(() => {
    return statusData.current.nextRefreshTime;
  }, [statusData.current.nextRefreshTime]);

  const getStockData = useMemo(() => {
    return getStocksTotalValue(equitiesETFData, data, modifiedStocksData);
  }, [equitiesETFData, modifiedStocksData, data]);

  const stocksAccounts = useMemo(() => {
    return statusData.current.accountDetails.filter((acc: any) => {
      return (
        (acc.fiType === 'EQUITIES' || acc.fiType === 'ETF') &&
        acc.status === 'ACTIVE'
      );
    });
  }, [statusData.current.accountDetails]);

  const isLoadingHoldings =
    showAnalytics && !holdingsData && !!stocksAccounts.length;

  const linkedStocksCount = useMemo(() => {
    return stocksAccounts.filter(
      (acc: any) => acc.lastRequestFIStatus === 'DELIVERED'
    ).length;
  }, [stocksAccounts]);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.onscroll = function () {
      if (window.scrollY > 18) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.scrollTo(0, 0);
  }, []);

  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const revokeConsent = () => {
    setIsButtonLoading(true);
    Promise.all(
      stocksAccounts
        .map((r: any) => r.referenceId)
        .map((ref: string) =>
          revokeConsentAPI({
            referenceId: ref,
          })
        )
    ).then(() => {
      setStocksData([]);
      data.current = { ...data.current, EQUITIES_ETF: undefined };
      setRevokeConsentSheet(false);
      setIsRevokeSheetOpen(false);
      setAnalyticsStatusStocks('PENDING');
      setStatusPolling((prev: any) => prev + 1);
      setIsButtonLoading(false);
    });
  };

  const [revokeConsentSheet, setRevokeConsentSheet] = useState(false);
  const [isRevokeSheetOpen, setIsRevokeSheetOpen] = useState(false);

  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [selectedCaps, setSelectedCaps] = useState<string[]>([]);
  const [selectedSorting, setSelectedSorting] = useState<string | null>(null);

  const [filterTab, setFilterTab] = useState('Sorting');
  const sortingOptions = [
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'currentValue', label: 'Current Value' },
    { value: 'change', label: '% Change (vs last week)' },
  ];

  const getMarketCapFromValue = (marketCap: string): string => {
    switch (marketCap?.toUpperCase()) {
      case 'SMALL_CAP':
      case 'SMALLCAP':
        return 'Small Cap';
      case 'MID_CAP':
      case 'MIDCAP':
        return 'Mid Cap';
      case 'LARGE_CAP':
      case 'LARGECAP':
        return 'Large Cap';
      default:
        return marketCap || 'Other';
    }
  };

  const filteredAndSortedHoldings = useMemo(() => {
    if (!holdingsData?.holdings) return [];

    let filtered = [...holdingsData.holdings];

    if (selectedCaps.length > 0) {
      filtered = filtered.filter((holding: any) => {
        const holdingMarketCap = getMarketCapFromValue(holding.marketCap);
        return selectedCaps.includes(holdingMarketCap);
      });
    }

    if (selectedSorting) {
      filtered.sort((a: any, b: any) => {
        let comparison = 0;

        switch (selectedSorting) {
          case 'name':
            comparison = (a.companyName || '').localeCompare(
              b.companyName || ''
            );
            break;

          case 'currentValue':
            const aValue =
              (Number.isNaN(parseInt(a.bsePrices))
                ? 0
                : parseInt(a.bsePrices)) * a.units;
            const bValue =
              (Number.isNaN(parseInt(b.bsePrices))
                ? 0
                : parseInt(b.bsePrices)) * b.units;
            comparison = bValue - aValue;
            break;

          case 'change':
            const aChange = a.percentageChange || 0;
            const bChange = b.percentageChange || 0;
            comparison = bChange - aChange;
            break;
        }

        return comparison;
      });
    }

    return filtered;
  }, [holdingsData?.holdings, selectedCaps, selectedSorting]);
  const clearAllFilters = () => {
    setSelectedCaps([]);
    setSelectedSorting(null);
  };

  useEffect(() => {
    window.onpopstate = () => {
      window.history.pushState(null, '', window.location.href);
    };

    setBackButtonHandler(() => {
      return () => {
        if (selectedHolding) {
          setSelectedHolding(null);
        } else if (isRevokeSheetOpen) {
          setIsRevokeSheetOpen(false);
        } else if (revokeConsentSheet) {
          setRevokeConsentSheet(false);
        } else if (showFilterSheet) {
          setShowFilterSheet(false);
        } else if (isAssetDashboardRendered.current) {
          navigate('/dashboard');
          setCurrentRoute(ROUTES.DASHBOARD);
        } else {
          pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
        }
      };
    });
  }, [selectedHolding, isRevokeSheetOpen, revokeConsentSheet, showFilterSheet]);

  if (selectedHolding) {
    return (
      <HoldingsScreen
        holdingData={selectedHolding}
        onBack={() => setSelectedHolding(null)}
      />
    );
  }
  return (
    <div className={styles.spends_home_container}>
      <Sheet
        className='accountDetailsSheet'
        isOpen={isRevokeSheetOpen}
        detent='content-height'
        onClose={() => {
          setIsRevokeSheetOpen(false);
        }}
      >
        <Sheet.Backdrop
          onTap={() => setIsRevokeSheetOpen(false)}
        ></Sheet.Backdrop>
        <Sheet.Container>
          <div className={styles.accountDetails}>
            <div className={styles.closeIconContainer}>
              <div
                className={styles.closeIcon}
                onClick={() => {
                  setIsRevokeSheetOpen(false);
                }}
              >
                <Typography.Text
                  style={{ color: 'var(--ct-bg-white)', fontWeight: 500 }}
                >
                  X
                </Typography.Text>
              </div>
            </div>

            <HeaderSubHeader
              header='Do you really want to revoke the consent?'
              subHeader='On revoking, you will not be able to track your demat accounts.'
            />

            <div className={styles.consentRevokeButtonContainer}>
              <Button
                className={styles.consentRevokeGoBack}
                onClick={() => {
                  pushEventToParent({
                    eventCode: EVENTCODES.GO_BACK_CONSENT_REVOKE,
                  });
                  setRevokeConsentSheet(true);
                  setIsRevokeSheetOpen(false);
                }}
              >
                <Typography.Text
                  style={{
                    fontWeight: 600,
                    color: 'var(--primary-color)',
                  }}
                >
                  Go Back
                </Typography.Text>
              </Button>
              <Button
                loading={isButtonLoading}
                className={styles.consentRevokeConfirm}
                onClick={() => {
                  if (dataRefreshStatus === RefreshStatus.IN_PROGRESS) {
                    message.info(
                      <Typography.Text
                        style={{ fontSize: 10, fontWeight: 600 }}
                      >
                        Your account data refresh is in progress. Please try
                        again in some time.
                      </Typography.Text>
                    );
                  } else {
                    pushEventToParent({
                      eventCode: EVENTCODES.CONSENT_REVOKE_CLICKED,
                    });
                    revokeConsent();
                  }
                }}
              >
                <Typography.Text
                  style={{ color: 'var(--ct-bg-white)', fontWeight: 600 }}
                >
                  Revoke
                </Typography.Text>
              </Button>
            </div>
          </div>
        </Sheet.Container>
      </Sheet>
      <Sheet
        isOpen={revokeConsentSheet}
        detent='content-height'
        className='pbRevokeConsentSheet'
        onClose={() => {
          setRevokeConsentSheet(false);
        }}
      >
        <Sheet.Backdrop
          onTap={() => setRevokeConsentSheet(false)}
        ></Sheet.Backdrop>

        <Sheet.Container>
          <div className={styles.consentRevokeSheetContainer}>
            <div className={styles.closeIconContainer}>
              <div
                className={styles.closeIcon}
                onClick={() => {
                  setRevokeConsentSheet(false);
                }}
              >
                <Typography.Text
                  style={{ color: 'var(--ct-bg-white)', fontWeight: 500 }}
                >
                  X
                </Typography.Text>
              </div>
            </div>

            <HeaderSubHeader
              header='Account Details'
              subHeader='Linked Stock accounts'
            />

            <div
              style={{
                margin: '14px 0px 0px 0px',
                overflow: 'auto',
                height: '50%',
                display: 'flex',
                flexDirection: 'column',
                paddingBottom: 1,
                gap: 12,
              }}
            >
              {modifiedStocksData.map((stocks: any) => (
                <div className={styles.linkedAccountItem}>
                  <SheetLinkedAccountItem
                    fipId={stocks.fipId}
                    fipName={stocks.rawData.fipName}
                    accNo={`XXXX ${stocks?.rawData?.maskedDematId?.slice(-4)}`}
                    showImage={true}
                  />{' '}
                  <Typography.Text
                    className={styles.revokeLink}
                    onClick={() => {
                      setRevokeConsentSheet(false);
                      setIsRevokeSheetOpen(true);
                      pushEventToParent({
                        eventCode: EVENTCODES.REVOKE_YOUR_CONSENT_CLICKED,
                      });
                    }}
                  >
                    Revoke
                  </Typography.Text>
                </div>
              ))}

              {stocksAccounts
                .filter((acc: any) => acc.lastRequestFIStatus !== 'DELIVERED')
                .map((acc: any) => (
                  <div className={styles.accountDetailsFailure}>
                    <div
                      style={{
                        flex: '70%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography.Text className={styles.fipTitle}>
                        {acc.fipName}
                      </Typography.Text>

                      <Typography.Text className={styles.accountNumberText}>
                        XXXX {acc.maskedAccountNumber.slice(-4)}
                      </Typography.Text>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        flex: '30%',
                      }}
                    >
                      <Popover
                        content={
                          <Typography.Text
                            style={{ fontSize: 12, fontWeight: 600 }}
                          >
                            We were unable to fetch details from your repository
                          </Typography.Text>
                        }
                        trigger='click'
                        overlayClassName={styles.popOverStyle}
                      >
                        <InfoCircleOutlined
                          style={{
                            width: 24,
                            height: 24,
                            display: 'flex',
                            justifyContent: 'center',
                            color: 'var(--primary-color)',
                          }}
                        />
                      </Popover>

                      <Typography.Text className={styles.accountNumberText}>
                        Data Fetch failed
                      </Typography.Text>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Sheet.Container>
      </Sheet>
      <NavigationHeader
        header='Stocks'
        onBack={() => {
          if (isRevokeSheetOpen) {
            setIsRevokeSheetOpen(false);
          } else if (revokeConsentSheet) {
            setRevokeConsentSheet(false);
          } else if (isAssetDashboardRendered.current) {
            navigate('/dashboard');
            setCurrentRoute(ROUTES.DASHBOARD);
          } else {
            pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
          }
        }}
      />
      <div style={{ padding: '0px 16px' }}>
        <AssetTopSection
          label='Your Total Stocks Value'
          amount={getStockData}
          isAmountVisible={isAmountVisible}
          linkedCount={linkedStocksCount}
          linkedLabel={`Linked account${linkedStocksCount !== 1 ? 's' : ''}`}
          refreshAccounts={stocksAccounts}
          onLinkedClick={() => setRevokeConsentSheet(true)}
          buttonText='+ Link More'
          onButtonClick={() => {
            pushEventToParent({ eventCode: EVENTCODES.ADD_ACCOUNT_STOCKS });
            startAdditionalAccountJourney(FlowType.STOCKS);
          }}
          formatAmount={(amt) => (
            <>
              <span
                style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500 }}
              >
                ₹
              </span>
              {formatToINRForStocks(amt.toString())}
            </>
          )}
        />

        {isLoadingHoldings ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              height: 200,
              marginTop: 100,
            }}
          >
            <img
              src={LoadingGIF}
              width={300}
              height={170}
              style={{
                transform: 'scaleX(-1)',
              }}
            />
            <Typography.Text
              style={{
                textAlign: 'center',
                color: 'var(--ct-text-muted)',
                fontSize: 12,
                fontWeight: 500,
                marginTop: -20,
              }}
            >
              Generating your personalized insights. This won't take long!
            </Typography.Text>
          </div>
        ) : (
          <>
            {showAnalytics && (
              <div
                style={{
                  background: 'var(--ct-bg-white)',
                  borderRadius: 8,
                  marginTop: 24,
                }}
              >
                <HeaderSubHeader header='My Holdings' />
                <div
                  style={{
                    display: 'flex',
                    overflow: 'hidden',
                    height: 10,
                    margin: '16px 0',
                  }}
                >
                  {Object.entries(holdingsData?.marketCap || {}).map(
                    ([key, value], index) => {
                      const chartColors = getChartColors();
                      const capColor = chartColors[index % chartColors.length];
                      const isLast =
                        index ===
                        Object.keys(holdingsData?.marketCap || {}).length - 1;

                      return (
                        <div
                          key={key}
                          style={{
                            width: String(value),
                            background: capColor,
                            height: 10,
                            borderRadius: 2,
                            marginRight: isLast ? 0 : 4,
                          }}
                        />
                      );
                    }
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  {Object.entries(holdingsData?.marketCap || {}).map(
                    ([key, value], index) => {
                      const capType = marketCapKeyToDisplay[key] || key;
                      const chartColors = getChartColors();
                      const capColor = chartColors[index % chartColors.length];
                      const capPercent = String(value || '0%');
                      return (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              background: capColor,
                              borderRadius: '50%',
                              display: 'inline-block',
                            }}
                          />
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <span style={{ fontSize: 12 }}>{capType}</span>
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: 12,
                                marginTop: key === 'SMALL_CAP' ? 2 : undefined,
                                marginLeft: key !== 'SMALL_CAP' ? 4 : undefined,
                              }}
                            >
                              {capPercent}
                            </span>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            {/* Stocks Count and Filter Button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: showAnalytics ? 'space-between' : 'flex-start',
                marginTop: 24,
              }}
            >
              <span
                style={{
                  color: 'var(--ct-text-muted)',
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: 1,
                }}
              >
                {`${filteredAndSortedHoldings.length} ${
                  filteredAndSortedHoldings.length === 1 ? 'STOCK' : 'STOCKS'
                }`}
              </span>

              {showAnalytics && (
                <button
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    border: '1px solid var(--ct-border-light)',
                    borderRadius: 20,
                    background: 'var(--ct-bg-white)',
                    padding: '6px 16px',
                    fontWeight: 500,
                    fontSize: 12,
                    cursor: 'pointer',
                    color: 'var(--ct-text-primary)',
                  }}
                  onClick={() => setShowFilterSheet(true)}
                >
                  <FilterSorting style={{ width: 16, height: 16 }} />
                  Filters & Sorting
                </button>
              )}
            </div>

            {/* Filter/Sorting Sheet */}
            {showFilterSheet && (
              <Sheet
                isOpen={showFilterSheet}
                detent='content-height'
                className='revokeConsentSheet'
                onClose={() => setShowFilterSheet(false)}
              >
                <Sheet.Backdrop onTap={() => setShowFilterSheet(false)} />
                <Sheet.Container>
                  <div className={styles.closeIconContainer}>
                    <div
                      className={styles.closeIcon}
                      onClick={() => setShowFilterSheet(false)}
                    >
                      <Typography.Text
                        style={{ color: 'var(--ct-bg-white)', fontWeight: 500 }}
                      >
                        X
                      </Typography.Text>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '30px 24px 16px',
                    }}
                  >
                    <HeaderSubHeader header='Filters & Sorting' />
                    <Typography.Text
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--ct-text-faint)',
                        cursor: 'pointer',
                      }}
                      onClick={clearAllFilters}
                    >
                      Clear All
                    </Typography.Text>
                  </div>

                  <div className={styles.filterSheetDivider} />

                  <div className={styles.filterSheetMain}>
                    <div className={styles.filterSheetSidebar}>
                      {['Filters', 'Sorting'].map((tab) => (
                        <button
                          key={tab}
                          className={
                            styles.filterSheetTab +
                            (filterTab === tab ? ` ${styles.selected}` : '')
                          }
                          onClick={() => setFilterTab(tab)}
                        >
                          {filterTab === tab && (
                            <span className={styles.filterSheetTabDot} />
                          )}
                          {tab}
                        </button>
                      ))}
                    </div>

                    <div className={styles.filterSheetContent}>
                      {filterTab === 'Sorting' &&
                        sortingOptions.map((opt) => (
                          <label
                            key={opt.value}
                            className={styles.filterSheetCheckbox}
                          >
                            <input
                              type='radio'
                              name='sortingGroup'
                              className={styles.input_checkbox}
                              checked={selectedSorting === opt.value}
                              onChange={() => setSelectedSorting(opt.value)}
                            />
                            {opt.label}
                          </label>
                        ))}

                      {filterTab === 'Filters' &&
                        ['Small Cap', 'Mid Cap', 'Large Cap'].map((cap) => (
                          <label
                            key={cap}
                            className={styles.filterSheetCheckbox}
                          >
                            <input
                              type='checkbox'
                              className={styles.input_checkbox}
                              checked={selectedCaps.includes(cap)}
                              onChange={(e) =>
                                setSelectedCaps((prev) =>
                                  e.target.checked
                                    ? [...prev, cap]
                                    : prev.filter((v) => v !== cap)
                                )
                              }
                            />
                            {cap}
                          </label>
                        ))}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: '16px 24px',
                      boxShadow: '0px -2px 14px 0px #0000001A',
                    }}
                  >
                    <button
                      className={styles.filterSheetApplyBtn}
                      onClick={() => setShowFilterSheet(false)}
                    >
                      Apply Filter
                    </button>
                  </div>
                </Sheet.Container>
              </Sheet>
            )}

            <div style={{ marginTop: 16 }}>
              {filteredAndSortedHoldings.map((holding: any, index: number) => {
                const currentValue =
                  (Number.isNaN(parseFloat(holding.bsePrices))
                    ? 0
                    : parseFloat(holding.bsePrices)) * holding.units;
                const investedValue = holding.investedValue || 0;
                const gainLoss =
                  investedValue > 0 ? currentValue - investedValue : 0;
                return (
                  <div
                    key={index}
                    className={styles.holdingCardContainer}
                    onClick={() => {
                      if (onHoldingClick) {
                        onHoldingClick(holding);
                      } else {
                        setSelectedHolding(holding);
                      }
                    }}
                  >
                    <div className={styles.holdingCard}>
                      <div className={styles.holdingCardLeft}>
                        <div className={styles.holdingIconContainer}>
                          <img
                            src={getStockLogoSrc(holding.logo, holding.isin)}
                            onError={handleImageError}
                            className={styles.holdingIconImg}
                            alt=''
                          />
                        </div>
                        <div className={styles.holdingCardInfo}>
                          <Typography.Text className={styles.holdingName}>
                            {holding.companyName}
                          </Typography.Text>
                          <div className={styles.holdingSubtitleRow}>
                            <span className={styles.holdingTypeBadge}>
                              {holding.sector || 'Equity'}
                            </span>
                            <Typography.Text className={styles.holdingSubtitle}>
                              {holding.units}{' '}
                              {holding.units > 1 ? 'units' : 'unit'}
                            </Typography.Text>
                          </div>
                        </div>
                      </div>
                      <div className={styles.holdingCardRight}>
                        <div className={styles.holdingAmountContainer}>
                          <Typography.Text className={styles.holdingAmount}>
                            {formatToINR(currentValue.toFixed(0), isAmountVisible)}
                          </Typography.Text>
                          {isAmountVisible && gainLoss !== 0 && (
                            <Typography.Text
                              className={
                                gainLoss >= 0
                                  ? styles.holdingGainPositive
                                  : styles.holdingGainNegative
                              }
                            >
                              {gainLoss >= 0 ? '+' : ''}
                              {formatToINR(gainLoss.toFixed(0), isAmountVisible)}
                            </Typography.Text>
                          )}
                        </div>
                        <RightArrow className={styles.holdingChevron} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StocksNewFlow;
